import type { Nullish, PriceId } from '@iheartjane/types';
import type { Offer } from 'schema-dts';

import { PRICE_ID_TO_GRAMS } from '../priceIdToGrams';

/** @see https://developers.google.com/search/docs/appearance/structured-data/product-snippet#offer-details */

interface PricingArgs {
  price_each?: Nullish<string | number>;
  price_eighth_ounce?: Nullish<string | number>;
  price_gram?: Nullish<string | number>;
  price_half_gram?: Nullish<string | number>;
  price_half_ounce?: Nullish<string | number>;
  price_ounce?: Nullish<string | number>;
  price_quarter_ounce?: Nullish<string | number>;
  price_two_gram?: Nullish<string | number>;
}

interface OffersArgs extends PricingArgs {
  currency_code: string;
  offer_sku?: Nullish<string | number>;
  offer_url?: Nullish<string>;
}

const PRICING_KEYS: (keyof PricingArgs)[] = [
  'price_each',
  'price_eighth_ounce',
  'price_gram',
  'price_half_gram',
  'price_half_ounce',
  'price_ounce',
  'price_quarter_ounce',
  'price_two_gram',
];

const weightName = (priceId: PriceId) => {
  const grams = PRICE_ID_TO_GRAMS[priceId];
  return grams ? `${grams}g` : 'Each';
};

const weightUrl = (url: string, priceId: PriceId) =>
  `${url}${url.includes('?') ? '&' : '?'}defaultWeight=${priceId}`;

/**
 * Note: this is a minimal implementation of an Offer schema, but can be expanded to represent more pricing details:
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
 * @see https://schema.org/Offer
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing#sale-pricing-example for discounted pricing
 * @see https://developers.google.com/search/docs/appearance/structured-data/merchant-listing#member-price-example for member pricing (ie. Jane Gold)
 */
export const offers = ({
  currency_code,
  offer_sku,
  offer_url,
  ...pricing
}: OffersArgs): { offers: Offer[] } => {
  const priced = PRICING_KEYS.flatMap((key) => {
    const weightedPrice = pricing[key];
    if (!weightedPrice) return [];
    const price =
      typeof weightedPrice === 'number'
        ? weightedPrice
        : parseFloat(weightedPrice);
    if (!Number.isFinite(price)) return [];
    return [{ price, priceId: key.replace('price_', '') as PriceId }];
  });

  if (priced.length === 0) {
    /**
     * When unavailable, an offer of price 0 must be provided
     * and availability must be set to out of stock or something similar.
     */
    return {
      offers: [
        {
          '@type': 'Offer',
          availability: 'https://schema.org/OutOfStock',
          price: '0',
          priceCurrency: currency_code,
        },
      ],
    };
  }

  return {
    offers: priced.map(({ price, priceId }) => ({
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      name: weightName(priceId),
      price,
      priceCurrency: currency_code,
      ...(offer_sku ? { sku: `${offer_sku}-${priceId}` } : {}),
      ...(offer_url ? { url: weightUrl(offer_url, priceId) } : {}),
    })),
  };
};
